import type { PricingRule, PricingRuleInput } from "@kuroo/sdk";

export type PricingRuleFormState = {
  id?: string;
  label: string;
  priority: string;
  enabled: boolean;
  pricingMode: "percentage" | "fixed";
  adjustmentPercent: string;
  fixedUnitPrice: string;
  minOccupancyPercent: string;
  maxOccupancyPercent: string;
  minLeadMinutes: string;
  maxLeadMinutes: string;
  weekdays: number[];
  dateFrom: string;
  dateUntil: string;
  timeFrom: string;
  timeUntil: string;
  timezone: string;
  minUnitPrice: string;
  maxUnitPrice: string;
};

export const weekdayLabels = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const weekdayFullLabels = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

function defaultTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Argentina/Buenos_Aires";
  } catch {
    return "America/Argentina/Buenos_Aires";
  }
}

export function emptyPricingRuleForm(): PricingRuleFormState {
  return {
    label: "",
    priority: "100",
    enabled: false,
    pricingMode: "percentage",
    adjustmentPercent: "0",
    fixedUnitPrice: "",
    minOccupancyPercent: "",
    maxOccupancyPercent: "",
    minLeadMinutes: "",
    maxLeadMinutes: "",
    weekdays: [],
    dateFrom: "",
    dateUntil: "",
    timeFrom: "",
    timeUntil: "",
    timezone: defaultTimezone(),
    minUnitPrice: "",
    maxUnitPrice: ""
  };
}

export function pricingRuleForm(rule: PricingRule): PricingRuleFormState {
  const adjustmentPercent = rule.adjustmentBps / 100;
  const pricingMode = rule.fixedUnitPriceCents == null ? "percentage" : "fixed";
  return {
    id: rule.id,
    label: rule.label ?? "",
    priority: String(rule.priority),
    enabled: rule.enabled,
    pricingMode,
    adjustmentPercent: pricingMode === "fixed" ? "0" : Number.isInteger(adjustmentPercent) ? String(adjustmentPercent) : adjustmentPercent.toFixed(2),
    fixedUnitPrice: rule.fixedUnitPriceCents == null ? "" : (rule.fixedUnitPriceCents / 100).toFixed(2),
    minOccupancyPercent: rule.minOccupancyPercent == null ? "" : String(rule.minOccupancyPercent),
    maxOccupancyPercent: rule.maxOccupancyPercent == null ? "" : String(rule.maxOccupancyPercent),
    minLeadMinutes: rule.minLeadMinutes == null ? "" : String(rule.minLeadMinutes),
    maxLeadMinutes: rule.maxLeadMinutes == null ? "" : String(rule.maxLeadMinutes),
    weekdays: rule.weekdays ? [...rule.weekdays] : [],
    dateFrom: rule.dateFrom ?? "",
    dateUntil: rule.dateUntil ?? "",
    timeFrom: rule.timeFrom ?? "",
    timeUntil: rule.timeUntil ?? "",
    timezone: rule.timezone ?? defaultTimezone(),
    minUnitPrice: rule.minUnitPriceCents == null ? "" : (rule.minUnitPriceCents / 100).toFixed(2),
    maxUnitPrice: rule.maxUnitPriceCents == null ? "" : (rule.maxUnitPriceCents / 100).toFixed(2)
  };
}

function optionalNumber(value: string, min: number, max: number): number | null | undefined {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= min && parsed <= max ? parsed : undefined;
}

function optionalInteger(value: string, min: number, max: number): number | null | undefined {
  const parsed = optionalNumber(value, min, max);
  return parsed === null || parsed === undefined || Number.isInteger(parsed) ? parsed : undefined;
}

function optionalCents(value: string): number | null | undefined {
  if (!value.trim()) return null;
  const parsed = Number(value);
  const cents = Math.round(parsed * 100);
  return Number.isFinite(parsed) && parsed >= 0 && Number.isInteger(cents) && cents <= 2_000_000_000 ? cents : undefined;
}

export function formatMoney(cents: number, currency = "ARS"): string {
  return new Intl.NumberFormat("es-AR", { style: "currency", currency }).format(cents / 100);
}

export function formatAdjustment(adjustmentBps: number): string {
  const percent = adjustmentBps / 100;
  const value = new Intl.NumberFormat("es-AR", { maximumFractionDigits: 2 }).format(Math.abs(percent));
  return `${percent >= 0 ? "+" : "-"}${value}%`;
}

export function ruleSchedule(rule: PricingRule): string {
  const days = rule.weekdays?.length ? rule.weekdays.map((day) => weekdayFullLabels[day] ?? `Día ${day}`).join(", ") : "Todos los días";
  const time = rule.timeFrom || rule.timeUntil ? `${rule.timeFrom || "00:00"}–${rule.timeUntil || "23:59"}` : "Todo el día";
  const dates = rule.dateFrom || rule.dateUntil ? ` · ${rule.dateFrom || "sin inicio"} → ${rule.dateUntil || "sin fin"}` : "";
  return `${days} · ${time}${dates}`;
}

export function ruleConditions(rule: PricingRule): string {
  const conditions: string[] = [];
  if (rule.minOccupancyPercent != null || rule.maxOccupancyPercent != null) {
    conditions.push(`ocupación ${rule.minOccupancyPercent ?? 0}–${rule.maxOccupancyPercent ?? 100}%`);
  }
  if (rule.minLeadMinutes != null || rule.maxLeadMinutes != null) {
    conditions.push(`anticipación ${rule.minLeadMinutes ?? 0}–${rule.maxLeadMinutes ?? "∞"} min`);
  }
  return conditions.length ? conditions.join(" · ") : "Sin filtros de ocupación o anticipación";
}

export function ruleValue(rule: PricingRule, currency?: string): string {
  return rule.fixedUnitPriceCents == null ? formatAdjustment(rule.adjustmentBps) : `Precio fijo ${formatMoney(rule.fixedUnitPriceCents, currency)}`;
}

export function formSchedule(form: PricingRuleFormState, currency?: string): string {
  const days = form.weekdays.length ? form.weekdays.map((day) => weekdayFullLabels[day] ?? `Día ${day}`).join(", ") : "todos los días";
  const time = form.timeFrom || form.timeUntil ? `${form.timeFrom || "00:00"}–${form.timeUntil || "23:59"}` : "todo el día";
  const adjustment = Number(form.adjustmentPercent);
  const fixedPrice = Number(form.fixedUnitPrice);
  const hasFixedPrice = form.fixedUnitPrice.trim() !== "";
  const amount = form.pricingMode === "fixed"
    ? hasFixedPrice && Number.isFinite(fixedPrice) && fixedPrice >= 0 ? `precio fijo de ${formatMoney(Math.round(fixedPrice * 100), currency)}` : "el precio fijo indicado"
    : Number.isFinite(adjustment) ? `${adjustment >= 0 ? "+" : ""}${adjustment}%` : "el ajuste indicado";
  return `Aplicar ${amount} los ${days}, ${time}.`;
}

export function buildServicePricingRuleInput(form: PricingRuleFormState, targetId: string): { input?: PricingRuleInput; error?: string } {
  const priority = optionalInteger(form.priority, -100_000, 100_000);
  const adjustmentPercent = form.pricingMode === "percentage" ? optionalNumber(form.adjustmentPercent, -100, 1_000) : 0;
  const adjustmentBps = form.pricingMode === "fixed" ? 0 : adjustmentPercent == null ? adjustmentPercent : Math.round(adjustmentPercent * 100);
  const fixedUnitPriceCents = form.pricingMode === "fixed" ? optionalCents(form.fixedUnitPrice) : null;
  const minOccupancyPercent = optionalInteger(form.minOccupancyPercent, 0, 100);
  const maxOccupancyPercent = optionalInteger(form.maxOccupancyPercent, 0, 100);
  const minLeadMinutes = optionalInteger(form.minLeadMinutes, 0, 10_512_000);
  const maxLeadMinutes = optionalInteger(form.maxLeadMinutes, 0, 10_512_000);
  const minUnitPriceCents = form.pricingMode === "percentage" ? optionalCents(form.minUnitPrice) : null;
  const maxUnitPriceCents = form.pricingMode === "percentage" ? optionalCents(form.maxUnitPrice) : null;

  if ([priority, adjustmentPercent, fixedUnitPriceCents, minOccupancyPercent, maxOccupancyPercent, minLeadMinutes, maxLeadMinutes, minUnitPriceCents, maxUnitPriceCents].some((value) => value === undefined)) {
    return { error: "Revisá los límites numéricos de la regla." };
  }
  if (form.pricingMode === "fixed" && fixedUnitPriceCents === null) {
    return { error: "Indicá el precio fijo por unidad para guardar la regla." };
  }
  if (minOccupancyPercent !== null && maxOccupancyPercent !== null && minOccupancyPercent !== undefined && maxOccupancyPercent !== undefined && minOccupancyPercent > maxOccupancyPercent) {
    return { error: "La ocupación mínima no puede superar la máxima." };
  }
  if (minLeadMinutes !== null && maxLeadMinutes !== null && minLeadMinutes !== undefined && maxLeadMinutes !== undefined && minLeadMinutes > maxLeadMinutes) {
    return { error: "La anticipación mínima no puede superar la máxima." };
  }
  if (form.dateFrom && form.dateUntil && form.dateFrom > form.dateUntil) {
    return { error: "La fecha inicial no puede ser posterior a la final." };
  }
  if (minUnitPriceCents !== null && maxUnitPriceCents !== null && minUnitPriceCents !== undefined && maxUnitPriceCents !== undefined && minUnitPriceCents > maxUnitPriceCents) {
    return { error: "El precio mínimo no puede superar el máximo." };
  }

  return {
    input: {
      targetKind: "service",
      targetId,
      priority: priority ?? 0,
      enabled: form.enabled,
      minOccupancyPercent: minOccupancyPercent ?? null,
      maxOccupancyPercent: maxOccupancyPercent ?? null,
      minLeadMinutes: minLeadMinutes ?? null,
      maxLeadMinutes: maxLeadMinutes ?? null,
      weekdays: form.weekdays.length ? form.weekdays : null,
      dateFrom: form.dateFrom || null,
      dateUntil: form.dateUntil || null,
      timeFrom: form.timeFrom || null,
      timeUntil: form.timeUntil || null,
      timezone: form.timezone.trim() || null,
      adjustmentBps: adjustmentBps ?? 0,
      fixedUnitPriceCents: fixedUnitPriceCents ?? null,
      minUnitPriceCents: minUnitPriceCents ?? null,
      maxUnitPriceCents: maxUnitPriceCents ?? null,
      label: form.label.trim() || null
    }
  };
}

export function errorMessage(reason: unknown): string {
  return reason instanceof Error ? reason.message : "No se pudo guardar la regla de precio.";
}
